import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Delete, Pencil, Plus, Search, Wallet } from 'lucide-react'

import { useDebounce } from '@/components/hooks/use-debounce'
import { formatMoney } from '@/lib/format'
import {
  EXPENSE_CATEGORIES,
  INCOME_CATEGORIES,
  SOURCE_LABELS,
  type Transaction,
  type TransactionCategory,
  type TransactionType,
  type TransactionWriteBody,
} from '@/types/finance'
import {
  useCreateTransaction,
  useDeleteTransaction,
  useTransactions,
  useUpdateTransaction,
} from '../hooks'
import { TransactionDialog } from './transaction-dialog'

interface TransactionsTabProps {
  from?: string
  to?: string
}

const typeBadgeVariant = (t: TransactionType) => (t === 'income' ? 'default' : 'secondary')
const ALL = 'all'

export function TransactionsTab({ from, to }: TransactionsTabProps) {
  const [typeFilter, setTypeFilter] = useState<TransactionType | typeof ALL>(ALL)
  const [categoryFilter, setCategoryFilter] = useState<TransactionCategory | typeof ALL>(ALL)
  const [search, setSearch] = useState('')
  const q = useDebounce(search, 300)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Transaction | null>(null)
  const [deleting, setDeleting] = useState<Transaction | null>(null)

  const filters = {
    from,
    to,
    type: typeFilter === ALL ? undefined : typeFilter,
    category: categoryFilter === ALL ? undefined : categoryFilter,
    q: q || undefined,
  }
  const { data: transactions, isLoading } = useTransactions(filters)
  const createMut = useCreateTransaction()
  const updateMut = useUpdateTransaction()
  const deleteMut = useDeleteTransaction()

  const submit = (body: TransactionWriteBody) => {
    if (editing) {
      updateMut.mutate({ id: editing.id, body }, { onSuccess: () => setDialogOpen(false) })
    } else {
      createMut.mutate(body, { onSuccess: () => setDialogOpen(false) })
    }
  }

  const categoryOptions =
    typeFilter === 'income'
      ? INCOME_CATEGORIES
      : typeFilter === 'expense'
        ? EXPENSE_CATEGORIES
        : [...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label className="text-muted-foreground">Type</Label>
          <Select
            value={typeFilter}
            onValueChange={(v) => {
              setTypeFilter(v as TransactionType | typeof ALL)
              setCategoryFilter(ALL)
            }}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              <SelectItem value="income">Income</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-muted-foreground">Category</Label>
          <Select
            value={categoryFilter}
            onValueChange={(v) => setCategoryFilter(v as TransactionCategory | typeof ALL)}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              {categoryOptions.map((c) => (
                <SelectItem key={c} value={c}>
                  {c.replace(/_/g, ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative min-w-56 flex-1 space-y-1.5">
          <Label className="text-muted-foreground">Search</Label>
          <Search className="absolute bottom-2.5 left-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Description contains…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button
          onClick={() => {
            setEditing(null)
            setDialogOpen(true)
          }}
        >
          <Plus className="h-4 w-4" /> Add entry
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead className="hidden sm:table-cell">Description</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="hidden md:table-cell">Source</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(6)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : !transactions || transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                  <Wallet className="mx-auto mb-2 h-6 w-6 opacity-50" />
                  No ledger entries match these filters.
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell className="whitespace-nowrap tabular-nums">{tx.occurred_on}</TableCell>
                  <TableCell
                    className="hidden max-w-72 truncate sm:table-cell"
                    title={tx.description}
                  >
                    {tx.description || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={typeBadgeVariant(tx.type)}>
                      {tx.category.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground md:table-cell">
                    {SOURCE_LABELS[tx.source]}
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums font-medium ${
                      tx.type === 'income' ? 'text-success' : 'text-foreground'
                    }`}
                  >
                    {tx.type === 'income' ? '+' : '−'}
                    {formatMoney(tx.amount)}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setEditing(tx)
                          setDialogOpen(true)
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => setDeleting(tx)}
                      >
                        <Delete className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TransactionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSubmit={submit}
        pending={createMut.isPending || updateMut.isPending}
      />

      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete ledger entry?</DialogTitle>
            <DialogDescription>
              {deleting
                ? `${deleting.occurred_on} · ${formatMoney(deleting.amount)} — ${
                    deleting.description || deleting.category.replace(/_/g, ' ')
                  }`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() =>
                deleting && deleteMut.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
              }
            >
              {deleteMut.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
